import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiBarChart2, FiTrash,
} from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';
import { useSession } from '../contexts/SessionContext';

// ── Colores de estado ──────────────────────────────────────────────────────────
const BADGE_PROYECTO = {
  pendiente: 'badge',
  en_curso: 'badge-info',
  pausado: 'badge-warning',
  finalizado: 'badge-ok',
};

const BADGE_CRM = {
  prospecto: 'badge',
  contactado: 'badge-info',
  presupuesto_enviado: 'badge-warning',
  negociando: 'badge-warning',
  ganado: 'badge-ok',
  perdido: 'badge-danger',
};

const TIPOS_PROYECTO = ['obra', 'reparacion', 'mantenimiento', 'otro'];
const ESTADOS_PROYECTO = ['pendiente', 'en_curso', 'pausado', 'finalizado'];

const VACIO_PROYECTO = {
  titulo: '', clienteId: '', tipo: 'obra', estado: 'pendiente',
  descripcion: '', fechaInicio: '', fechaFinEstimada: '', notas: '',
};

const VACIO_PARTE = {
  proyectoId: '', usuarioId: '', fecha: '', horasInvertidas: '', descripcion: '',
};

const VACIO_PARTIDA = {
  codigo: '', nombre: '', descripcion: '', categoria: '', unidad: '',
  precioUnitario: '', iva: 21, activa: true,
};

const VACIO_PLANTILLA = { nombre: '', descripcion: '', lineas: [] };
const LINEA_PLANTILLA_VACIA = { partidaId: '', descripcion: '', cantidad: 1, precioUnitario: 0, iva: 21 };

const fmt = (d) => d ? new Date(d).toLocaleDateString('es-ES') : '—';

// ── Sub-componente: Editor de líneas de plantilla ─────────────────────────────
function LineasPlantillaEditor({ lineas, setLineas, partidas }) {
  const upd = (i, campo, val) =>
    setLineas((ls) => ls.map((l, idx) => (idx === i ? { ...l, [campo]: val } : l)));

  const elegirPartida = (i, id) => {
    const p = partidas.find((x) => x._id === id);
    setLineas((ls) => ls.map((l, idx) =>
      idx === i ? {
        ...l, partidaId: id,
        descripcion: p ? p.nombre : l.descripcion,
        precioUnitario: p ? p.precioUnitario : l.precioUnitario,
        iva: p ? p.iva : l.iva,
      } : l
    ));
  };

  return (
    <div className="span-2">
      <div className="table-wrap">
        <table className="data-table lineas-table">
          <thead>
            <tr>
              <th>Partida</th><th>Descripción</th><th>Cant.</th>
              <th>Precio</th><th>IVA%</th><th></th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={i}>
                <td>
                  <select value={l.partidaId} onChange={(e) => elegirPartida(i, e.target.value)}>
                    <option value="">—</option>
                    {partidas.map((p) => (
                      <option key={p._id} value={p._id}>{p.nombre}</option>
                    ))}
                  </select>
                </td>
                <td><input value={l.descripcion} onChange={(e) => upd(i, 'descripcion', e.target.value)} /></td>
                <td><input type="number" min="0" style={{ width: 60 }} value={l.cantidad} onChange={(e) => upd(i, 'cantidad', e.target.value)} /></td>
                <td><input type="number" step="0.01" style={{ width: 80 }} value={l.precioUnitario} onChange={(e) => upd(i, 'precioUnitario', e.target.value)} /></td>
                <td><input type="number" style={{ width: 55 }} value={l.iva} onChange={(e) => upd(i, 'iva', e.target.value)} /></td>
                <td>
                  <button type="button" className="icon-btn danger"
                    onClick={() => setLineas((ls) => ls.filter((_, idx) => idx !== i))}>
                    <FiTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btn-sm"
        onClick={() => setLineas((ls) => [...ls, { ...LINEA_PLANTILLA_VACIA }])}>
        <FiPlus /> Añadir línea
      </button>
    </div>
  );
}

// ── Tab Proyectos ─────────────────────────────────────────────────────────────
function TabProyectos({ usuario }) {
  const { items, cargando, error, crear, actualizar, eliminar, cargar } = useEntity('proyectos');
  const [clientes, setClientes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO_PROYECTO);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [rentModal, setRentModal] = useState(false);
  const [rentData, setRentData] = useState(null);
  const [rentProyecto, setRentProyecto] = useState('');

  const puedeCrear = puede(usuario, 'proyectos', 'crear');
  const puedeEditar = puede(usuario, 'proyectos', 'editar');
  const puedeEliminar = puede(usuario, 'proyectos', 'eliminar');

  useEffect(() => {
    api.get('/clientes').then(({ data }) => setClientes(data)).catch(() => {});
  }, []);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const abrirNuevo = () => { setEditando(null); setForm(VACIO_PROYECTO); setModal(true); };
  const abrirEditar = (p) => {
    setEditando(p);
    setForm({
      ...VACIO_PROYECTO, ...p,
      clienteId: p.clienteId?._id || p.clienteId || '',
      fechaInicio: p.fechaInicio ? p.fechaInicio.slice(0, 10) : '',
      fechaFinEstimada: p.fechaFinEstimada ? p.fechaFinEstimada.slice(0, 10) : '',
    });
    setModal(true);
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...form, clienteId: form.clienteId || null };
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const onEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.titulo}"?`)) return;
    try { await eliminar(p._id); } catch { alert('No se pudo eliminar'); }
  };

  const verRentabilidad = async (p) => {
    setRentProyecto(p.titulo);
    setRentData(null);
    setRentModal(true);
    try {
      const { data } = await api.get(`/proyectos/${p._id}/rentabilidad`);
      setRentData(data);
    } catch (err) {
      setRentData({ error: err.response?.data?.error || 'Error al cargar rentabilidad' });
    }
  };

  const nombreCliente = (c) => (typeof c === 'object' && c ? c.nombre : clientes.find((x) => x._id === c)?.nombre || '—');

  const filtrados = items.filter((p) =>
    [p.titulo, p.tipo, p.estado].join(' ').toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <>
      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input
            placeholder="Buscar por título, tipo o estado"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        {puedeCrear && (
          <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nuevo proyecto</button>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : filtrados.length === 0 ? (
        <p className="empty">No hay proyectos.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Título</th><th>Cliente</th><th>Tipo</th><th>Estado</th>
                <th>Fecha inicio</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((p) => (
                <tr key={p._id} onDoubleClick={() => abrirSesion('proyecto', p._id, p.titulo)} style={{ cursor: 'pointer' }}>
                  <td data-label="Cliente">{nombreCliente(p.clienteId)}</td>
                  <td data-label="Tipo">{p.tipo}</td>
                  <td data-label="Estado">
                    <span className={`badge ${BADGE_PROYECTO[p.estado] || 'badge'}`}>{p.estado.replace('_', ' ')}</span>
                  </td>
                  <td data-label="Fecha inicio">{fmt(p.fechaInicio)}</td>
                  <td className="acciones">
                    <button className="icon-btn" onClick={() => verRentabilidad(p)} title="Ver rentabilidad">
                      <FiBarChart2 />
                    </button>
                    {puedeEditar && (
                      <button className="icon-btn" onClick={() => abrirEditar(p)} title="Editar"><FiEdit2 /></button>
                    )}
                    {puedeEliminar && (
                      <button className="icon-btn danger" onClick={() => onEliminar(p)} title="Eliminar"><FiTrash2 /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar proyecto */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar proyecto' : 'Nuevo proyecto'} ancho={680}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field span-2"><span>Título *</span>
            <input required value={form.titulo} onChange={set('titulo')} />
          </label>
          <label className="field"><span>Cliente</span>
            <select value={form.clienteId} onChange={set('clienteId')}>
              <option value="">Sin cliente</option>
              {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre}</option>)}
            </select>
          </label>
          <label className="field"><span>Tipo</span>
            <select value={form.tipo} onChange={set('tipo')}>
              {TIPOS_PROYECTO.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="field"><span>Estado</span>
            <select value={form.estado} onChange={set('estado')}>
              {ESTADOS_PROYECTO.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </label>
          <label className="field"><span>Fecha inicio</span>
            <input type="date" value={form.fechaInicio} onChange={set('fechaInicio')} />
          </label>
          <label className="field"><span>Fecha fin estimada</span>
            <input type="date" value={form.fechaFinEstimada} onChange={set('fechaFinEstimada')} />
          </label>
          <label className="field span-2"><span>Descripción</span>
            <textarea rows={3} value={form.descripcion} onChange={set('descripcion')} />
          </label>
          <label className="field span-2"><span>Notas</span>
            <textarea rows={2} value={form.notas} onChange={set('notas')} />
          </label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal rentabilidad */}
      <Modal abierto={rentModal} onCerrar={() => setRentModal(false)} titulo={`Rentabilidad: ${rentProyecto}`} ancho={480}>
        {!rentData ? (
          <p className="empty">Cargando…</p>
        ) : rentData.error ? (
          <p className="alert-error">{rentData.error}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="stat-card">
                <p className="stat-label">Presupuestado</p>
                <p className="stat-value">{(rentData.presupuestado || 0).toFixed(2)} €</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Horas trabajadas</p>
                <p className="stat-value">{rentData.horasTrabajadas || 0} h</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Coste mano de obra est.</p>
                <p className="stat-value">{(rentData.costeManoObra || 0).toFixed(2)} €</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Margen bruto</p>
                <p className="stat-value" style={{ color: (rentData.margenBruto || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  {(rentData.margenBruto || 0).toFixed(2)} €
                  {rentData.margenPorcentaje != null && (
                    <span style={{ fontSize: 13, marginLeft: 6 }}>({rentData.margenPorcentaje.toFixed(1)}%)</span>
                  )}
                </p>
              </div>
            </div>
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setRentModal(false)}>Cerrar</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// ── Tab Partes de trabajo ─────────────────────────────────────────────────────
function TabPartes({ usuario }) {
  const { items, cargando, error, crear, actualizar, eliminar, cargar } = useEntity('partes-trabajo');
  const [proyectos, setProyectos] = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO_PARTE);
  const [guardando, setGuardando] = useState(false);

  const esEmpleado = usuario?.rol === 'empleado';
  const puedeCrear = puede(usuario, 'proyectos', 'crear');
  const puedeEditar = puede(usuario, 'proyectos', 'editar');
  const puedeEliminar = puede(usuario, 'proyectos', 'eliminar');

  useEffect(() => {
    api.get('/proyectos').then(({ data }) => setProyectos(data)).catch(() => {});
    api.get('/usuarios').then(({ data }) => setEquipo(data.filter((u) => u.rol !== 'superadmin'))).catch(() => {});
  }, []);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      ...VACIO_PARTE,
      usuarioId: esEmpleado ? usuario._id : '',
      fecha: new Date().toISOString().slice(0, 10),
    });
    setModal(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({
      ...VACIO_PARTE, ...p,
      proyectoId: p.proyectoId?._id || p.proyectoId || '',
      usuarioId: p.usuarioId?._id || p.usuarioId || '',
      fecha: p.fecha ? p.fecha.slice(0, 10) : '',
    });
    setModal(true);
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        ...form,
        proyectoId: form.proyectoId || null,
        usuarioId: esEmpleado ? usuario._id : form.usuarioId,
      };
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const onEliminar = async (p) => {
    if (!window.confirm('¿Eliminar este parte de trabajo?')) return;
    try { await eliminar(p._id); } catch { alert('No se pudo eliminar'); }
  };

  const nombreProyecto = (id) => {
    if (!id) return '—';
    const obj = typeof id === 'object' ? id : proyectos.find((x) => x._id === id);
    return obj?.titulo || '—';
  };
  const nombreEmpleado = (id) => {
    if (!id) return '—';
    const obj = typeof id === 'object' ? id : equipo.find((x) => x._id === id);
    return obj ? `${obj.nombre} ${obj.apellidos || ''}`.trim() : '—';
  };

  return (
    <>
      <div className="toolbar">
        {puedeCrear && (
          <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nuevo parte</button>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="empty">No hay partes de trabajo.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Fecha</th><th>Empleado</th><th>Proyecto</th><th>Horas</th><th>Descripción</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id}>
                  <td data-label="Fecha">{fmt(p.fecha)}</td>
                  <td data-label="Empleado">{nombreEmpleado(p.usuarioId)}</td>
                  <td data-label="Proyecto">{nombreProyecto(p.proyectoId)}</td>
                  <td data-label="Horas">{p.horasInvertidas}</td>
                  <td data-label="Descripción">{p.descripcion || '—'}</td>
                  <td className="acciones">
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(p)} title="Editar"><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(p)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar parte' : 'Nuevo parte de trabajo'} ancho={560}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field"><span>Fecha *</span>
            <input required type="date" value={form.fecha} onChange={set('fecha')} />
          </label>
          <label className="field"><span>Horas invertidas *</span>
            <input required type="number" step="0.25" min="0" value={form.horasInvertidas} onChange={set('horasInvertidas')} />
          </label>
          <label className="field"><span>Proyecto</span>
            <select value={form.proyectoId} onChange={set('proyectoId')}>
              <option value="">Sin proyecto</option>
              {proyectos.map((p) => <option key={p._id} value={p._id}>{p.titulo}</option>)}
            </select>
          </label>
          {!esEmpleado && (
            <label className="field"><span>Empleado *</span>
              <select required value={form.usuarioId} onChange={set('usuarioId')}>
                <option value="">Selecciona empleado</option>
                {equipo.map((u) => <option key={u._id} value={u._id}>{u.nombre} {u.apellidos}</option>)}
              </select>
            </label>
          )}
          <label className="field span-2"><span>Descripción</span>
            <textarea rows={3} value={form.descripcion} onChange={set('descripcion')} />
          </label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ── Tab Rendimiento ───────────────────────────────────────────────────────────
function TabRendimiento() {
  const hoy = new Date();
  const hace30 = new Date(hoy);
  hace30.setDate(hoy.getDate() - 30);
  const fmtISO = (d) => d.toISOString().slice(0, 10);

  const [desde, setDesde] = useState(fmtISO(hace30));
  const [hasta, setHasta] = useState(fmtISO(hoy));
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const { data } = await api.get(`/partes-trabajo/rendimiento?desde=${desde}&hasta=${hasta}`);
      setDatos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar rendimiento');
    } finally { setCargando(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <>
      <div className="toolbar">
        <label className="field" style={{ marginBottom: 0 }}>
          <span>Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button className="btn btn-primary" onClick={cargar}>Actualizar</button>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : datos.length === 0 ? (
        <p className="empty">Sin datos en ese periodo.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Empleado</th><th>Total horas</th><th>Proyectos distintos</th></tr>
            </thead>
            <tbody>
              {datos.map((d, i) => (
                <tr key={i}>
                  <td data-label="Empleado"><strong>{d.nombre || d.empleado || '—'}</strong></td>
                  <td data-label="Total horas">{d.totalHoras || 0} h</td>
                  <td data-label="Proyectos">{d.proyectosDistintos || d.proyectos || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Tab Partidas ──────────────────────────────────────────────────────────────
function TabPartidas({ usuario }) {
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('partidas');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO_PARTIDA);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const puedeCrear = puede(usuario, 'proyectos', 'crear');
  const puedeEditar = puede(usuario, 'proyectos', 'editar');
  const puedeEliminar = puede(usuario, 'proyectos', 'eliminar');

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const abrirNuevo = () => { setEditando(null); setForm(VACIO_PARTIDA); setModal(true); };
  const abrirEditar = (p) => { setEditando(p); setForm({ ...VACIO_PARTIDA, ...p }); setModal(true); };

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando) await actualizar(editando._id, form);
      else await crear(form);
      setModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const onEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la partida "${p.nombre}"?`)) return;
    try { await eliminar(p._id); } catch { alert('No se pudo eliminar'); }
  };

  const filtrados = items.filter((p) =>
    [p.nombre, p.categoria, p.codigo].join(' ').toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <>
      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input
            placeholder="Buscar por nombre o categoría"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        {puedeCrear && (
          <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nueva partida</button>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : filtrados.length === 0 ? (
        <p className="empty">No hay partidas.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Código</th><th>Nombre</th><th>Categoría</th><th>Unidad</th><th>Precio</th><th>IVA</th><th>Activa</th><th></th></tr>
            </thead>
            <tbody>
              {filtrados.map((p) => (
                <tr key={p._id}>
                  <td data-label="Código">{p.codigo || '—'}</td>
                  <td data-label="Nombre"><strong>{p.nombre}</strong></td>
                  <td data-label="Categoría">{p.categoria || '—'}</td>
                  <td data-label="Unidad">{p.unidad || '—'}</td>
                  <td data-label="Precio">{Number(p.precioUnitario || 0).toFixed(2)} €</td>
                  <td data-label="IVA">{p.iva}%</td>
                  <td data-label="Activa">
                    <span className={`badge ${p.activa ? 'badge-ok' : 'badge-danger'}`}>{p.activa ? 'Sí' : 'No'}</span>
                  </td>
                  <td className="acciones">
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(p)} title="Editar"><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(p)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar partida' : 'Nueva partida'} ancho={620}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field"><span>Código</span>
            <input value={form.codigo} onChange={set('codigo')} />
          </label>
          <label className="field"><span>Nombre *</span>
            <input required value={form.nombre} onChange={set('nombre')} />
          </label>
          <label className="field"><span>Categoría</span>
            <input value={form.categoria} onChange={set('categoria')} />
          </label>
          <label className="field"><span>Unidad</span>
            <input placeholder="ud, m², h…" value={form.unidad} onChange={set('unidad')} />
          </label>
          <label className="field"><span>Precio unitario (€)</span>
            <input type="number" step="0.01" min="0" value={form.precioUnitario} onChange={set('precioUnitario')} />
          </label>
          <label className="field"><span>IVA (%)</span>
            <input type="number" step="0.1" min="0" value={form.iva} onChange={set('iva')} />
          </label>
          <label className="field span-2"><span>Descripción</span>
            <textarea rows={2} value={form.descripcion} onChange={set('descripcion')} />
          </label>
          <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={!!form.activa}
              onChange={(e) => setForm((f) => ({ ...f, activa: e.target.checked }))}
            />
            <span>Activa</span>
          </label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ── Tab Plantillas ────────────────────────────────────────────────────────────
function TabPlantillas({ usuario }) {
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('plantillas-presupuesto');
  const [partidas, setPartidas] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO_PLANTILLA);
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [verModal, setVerModal] = useState(false);
  const [verPlantilla, setVerPlantilla] = useState(null);

  const puedeCrear = puede(usuario, 'proyectos', 'crear');
  const puedeEditar = puede(usuario, 'proyectos', 'editar');
  const puedeEliminar = puede(usuario, 'proyectos', 'eliminar');

  useEffect(() => {
    api.get('/partidas').then(({ data }) => setPartidas(data)).catch(() => {});
  }, []);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const abrirNuevo = () => {
    setEditando(null);
    setForm(VACIO_PLANTILLA);
    setLineas([]);
    setModal(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({ nombre: p.nombre, descripcion: p.descripcion || '' });
    setLineas(p.lineas ? p.lineas.map((l) => ({ ...LINEA_PLANTILLA_VACIA, ...l, partidaId: l.partidaId || '' })) : []);
    setModal(true);
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...form, lineas };
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const onEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try { await eliminar(p._id); } catch { alert('No se pudo eliminar'); }
  };

  return (
    <>
      <div className="toolbar">
        {puedeCrear && (
          <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nueva plantilla</button>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="empty">No hay plantillas.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Nombre</th><th>Descripción</th><th>N.º líneas</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id}>
                  <td data-label="Nombre"><strong>{p.nombre}</strong></td>
                  <td data-label="Descripción">{p.descripcion || '—'}</td>
                  <td data-label="N.º líneas">{(p.lineas || []).length}</td>
                  <td className="acciones">
                    <button className="icon-btn" onClick={() => { setVerPlantilla(p); setVerModal(true); }} title="Ver líneas">
                      <FiBarChart2 />
                    </button>
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(p)} title="Editar"><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(p)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar plantilla' : 'Nueva plantilla'} ancho={820}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field span-2"><span>Nombre *</span>
            <input required value={form.nombre} onChange={set('nombre')} />
          </label>
          <label className="field span-2"><span>Descripción</span>
            <textarea rows={2} value={form.descripcion} onChange={set('descripcion')} />
          </label>
          <LineasPlantillaEditor lineas={lineas} setLineas={setLineas} partidas={partidas} />
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal ver líneas */}
      <Modal abierto={verModal} onCerrar={() => setVerModal(false)} titulo={verPlantilla?.nombre || ''} ancho={680}>
        {verPlantilla && (
          <>
            {verPlantilla.descripcion && <p className="muted" style={{ marginBottom: 12 }}>{verPlantilla.descripcion}</p>}
            {(verPlantilla.lineas || []).length === 0 ? (
              <p className="empty">Sin líneas.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table lineas-table">
                  <thead>
                    <tr><th>Descripción</th><th>Cant.</th><th>Precio</th><th>IVA%</th></tr>
                  </thead>
                  <tbody>
                    {verPlantilla.lineas.map((l, i) => (
                      <tr key={i}>
                        <td>{l.descripcion || '—'}</td>
                        <td>{l.cantidad}</td>
                        <td>{Number(l.precioUnitario || 0).toFixed(2)} €</td>
                        <td>{l.iva}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn" onClick={() => setVerModal(false)}>Cerrar</button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
const TABS = [
  { k: 'proyectos', label: 'Proyectos' },
  { k: 'partes', label: 'Partes de trabajo' },
  { k: 'rendimiento', label: 'Rendimiento' },
  { k: 'partidas', label: 'Partidas' },
  { k: 'plantillas', label: 'Plantillas' },
];

export default function Proyectos() {
  const { usuario } = useAuth();
  const { abrirSesion } = useSession();
  const [tab, setTab] = useState('proyectos');

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Proyectos y obras</h1>
          <p className="page-subtitle">Gestión de proyectos, partes y biblioteca de partidas</p>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.k} className={`tab ${tab === t.k ? 'activo' : ''}`} onClick={() => setTab(t.k)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'proyectos' && <TabProyectos usuario={usuario} />}
      {tab === 'partes' && <TabPartes usuario={usuario} />}
      {tab === 'rendimiento' && <TabRendimiento />}
      {tab === 'partidas' && <TabPartidas usuario={usuario} />}
      {tab === 'plantillas' && <TabPlantillas usuario={usuario} />}
    </div>
  );
}
